import { Autocomplete, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Transaction, TransactionType } from '../../../../../services/transaction.types';
import { filterOptionsWithCreate, extractCreateValue } from '../../../../../utils/autocomplete';

interface TransactionHeaderProps {
    formValues: Partial<Transaction>;
    accounts: string[];
    selectedType: TransactionType | undefined;
    onDateChange: (date: Date | null) => void;
    onTimeChange: (time: Date | null) => void;
    onAccountChange: (value: string | null) => void;
    onTypeModalOpen: () => void;
}

const TransactionHeader: React.FC<TransactionHeaderProps> = (props) => {
    const { t, i18n } = useTranslation();
    const {
        formValues,
        accounts,
        selectedType,
        onDateChange,
        onTimeChange,
        onAccountChange,
        onTypeModalOpen,
    } = props;

    return (
        <>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr 1fr'
                },
                gap: 2
            }}>
                <Box>
                    <TextField
                        fullWidth
                        value={selectedType ? selectedType.name : ''}
                        label={t('transactions.modal.form.transactionType')}
                        onClick={onTypeModalOpen}
                        InputProps={{
                            readOnly: true,
                            sx: {
                                cursor: 'pointer',
                                '&.Mui-focused': {
                                    boxShadow: 'none'
                                }
                            },
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                borderRadius: 1
                            },
                        }}
                    />
                </Box>
                <Box>
                    <DatePicker
                        label={t('transactions.modal.form.date')}
                        value={formValues.date ? new Date(formValues.date * 1000) : null}
                        onChange={onDateChange}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                sx: {
                                    '& .MuiInputBase-root': {
                                        borderRadius: 1
                                    },
                                },
                            },
                        }}
                        localeText={{
                            toolbarTitle: t('transactions.modal.form.date'),
                            okButtonLabel: t('common.ok'),
                            cancelButtonLabel: t('common.cancel'),
                        }}
                        format="dd.MM.yyyy"
                    />
                </Box>
                <Box>
                    <TimePicker
                        label={t('transactions.modal.form.time')}
                        value={formValues.date ? new Date(formValues.date * 1000) : null}
                        onChange={onTimeChange}
                        views={['hours', 'minutes', 'seconds']}
                        format="HH:mm:ss"
                        ampm={false}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                sx: {
                                    '& .MuiInputBase-root': {
                                        borderRadius: 1
                                    },
                                },
                            },
                        }}
                        localeText={{
                            toolbarTitle: t('transactions.modal.form.time'),
                            okButtonLabel: t('common.ok'),
                            cancelButtonLabel: t('common.cancel'),
                        }}
                    />
                </Box>
                <Box>
                    <Autocomplete
                        value={formValues.account || null}
                        onChange={(_, value) => onAccountChange(extractCreateValue(value, i18n))}
                        options={accounts}
                        disablePortal
                        freeSolo
                        autoSelect
                        filterOptions={(options, params) => filterOptionsWithCreate(options, params, i18n)}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <li key={key} {...otherProps}>
                                    {option}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('transactions.modal.form.account')}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        borderRadius: 1
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </Box>
        </>
    );
};

export default TransactionHeader;
